<script lang="ts">
  import { Bodega, FarmaciaClinica, Bitacora, type Articulo, type Proveedor } from '../lib/api';
  import { parseSpreadsheet, csvCell } from '../lib/csv-import';

  let articulos = $state<Articulo[]>([]);
  let proveedores = $state<Proveedor[]>([]);
  let bajoMinimo = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);
  let loading = $state(false);

  let nuevoArt = $state<Articulo>({ codigo: '', nombre: '', unidad: 'UN', stockMinimo: 0, activo: true });
  let nuevoProv = $state<Proveedor>({ nombre: '', nit: '', telefono: '', email: '', activo: true });
  let mov = $state({ articuloId: '', tipo: 'ENTRADA' as const, cantidad: 1, costoUnitario: 0, referencia: '', motivo: '' });

  async function cargar() {
    loading = true; error = null;
    try {
      articulos = await Bodega.articulos(bajoMinimo);
      proveedores = await Bodega.proveedores();
    } catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  async function crearArticulo(e: SubmitEvent) {
    e.preventDefault();
    try { await Bodega.crearArt({ ...nuevoArt }); nuevoArt = { codigo: '', nombre: '', unidad: 'UN', stockMinimo: 0, activo: true }; await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  async function trasladarAFarmacia(a: Articulo) {
    if (!a.id) return;
    const raw = window.prompt('Cantidad a trasladar a Farmacia POS:', '1');
    if (raw == null) return;
    const cantidad = Number(raw);
    if (!Number.isFinite(cantidad) || cantidad <= 0) {
      error = 'Cantidad inválida';
      return;
    }
    error = null;
    info = null;
    try {
      await Bodega.mover({
        articuloId: a.id,
        tipo: 'SALIDA',
        cantidad,
        motivo: 'Traslado a farmacia POS',
      });
      const productos = await FarmaciaClinica.productos();
      const existente = productos.find(p => p.codigo.toLowerCase() === a.codigo.toLowerCase());
      if (existente?.id) {
        await FarmaciaClinica.actualizarProducto(existente.id, {
          ...existente,
          stock: Number(existente.stock ?? 0) + cantidad,
        });
      } else {
        await FarmaciaClinica.crearProducto({
          codigo: a.codigo,
          nombre: a.nombre,
          precio: Number(a.costoPromedio ?? 0),
          stock: cantidad,
          categoria: 'MEDICAMENTO',
          afectaInventario: true,
          activo: true,
        });
      }
      Bitacora.registrar({
        action: 'traslado.bodega_farmacia',
        resourceType: 'articulo',
        resourceId: a.id,
        details: { codigo: a.codigo, nombre: a.nombre, cantidad },
      }).catch(() => {});
      info = `Trasladado ${cantidad} de «${a.codigo}» a Farmacia POS.`;
      await cargar();
    } catch (e: any) {
      error = e?.message ?? String(e);
    }
  }

  async function importarCsvArticulos(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    error = null; info = null;
    try {
      const { rows } = await parseSpreadsheet(file);
      let ok = 0;
      let fail = 0;
      for (const row of rows) {
        const codigo = csvCell(row, 'codigo', 'code');
        const nombre = csvCell(row, 'nombre', 'name', 'articulo');
        if (!codigo || !nombre) { fail++; continue; }
        try {
          await Bodega.crearArt({
            codigo,
            nombre,
            unidad: csvCell(row, 'unidad', 'unit') || 'UN',
            stockMinimo: Number(csvCell(row, 'stockminimo', 'stock_minimo', 'minimo') || 0),
            activo: true,
          });
          ok++;
        } catch { fail++; }
      }
      info = `Importación: ${ok} artículo(s)${fail ? `, ${fail} omitido(s)` : ''}.`;
      await cargar();
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      input.value = '';
    }
  }

  async function crearProveedor(e: SubmitEvent) {
    e.preventDefault();
    try { await Bodega.crearProv({ ...nuevoProv }); nuevoProv = { nombre: '', nit: '', telefono: '', email: '', activo: true }; await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  async function registrarMov(e: SubmitEvent) {
    e.preventDefault();
    try { await Bodega.mover({ ...mov }); mov = { articuloId: '', tipo: 'ENTRADA', cantidad: 1, costoUnitario: 0, referencia: '', motivo: '' }; await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  $effect(() => { cargar(); });
</script>

<section>
  <header><h2>Bodega &middot; Compras &middot; Inventario</h2>
    <label class="chk"><input type="checkbox" bind:checked={bajoMinimo} onchange={cargar}/> Sólo bajo mínimos</label>
    <button onclick={cargar} disabled={loading}>{loading ? '…' : 'Recargar'}</button>
  </header>
  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if info}<p class="ok">{info}</p>{/if}

  <h3>Artículos</h3>
  <form onsubmit={crearArticulo} class="row">
    <input placeholder="Código" bind:value={nuevoArt.codigo} required />
    <input placeholder="Nombre" bind:value={nuevoArt.nombre} required style="flex:1;min-width:180px"/>
    <input placeholder="Unidad" bind:value={nuevoArt.unidad} style="width:80px"/>
    <input type="number" placeholder="Stock mín." bind:value={nuevoArt.stockMinimo} style="width:110px"/>
    <button type="submit">Agregar</button>
    <label class="csv-btn">
      Importar CSV / Excel
      <input type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onchange={importarCsvArticulos} hidden />
    </label>
  </form>
  <p class="hint">CSV o Excel (.xlsx): codigo,nombre,unidad,stockMinimo</p>
  <table>
    <thead><tr><th>Código</th><th>Nombre</th><th>Stock</th><th>Mínimo</th><th>Costo prom.</th><th></th></tr></thead>
    <tbody>
      {#each articulos as a (a.id)}
        <tr class:bajo={Number(a.stockActual ?? 0) <= Number(a.stockMinimo ?? 0)}>
          <td>{a.codigo}</td><td>{a.nombre}</td><td>{a.stockActual ?? 0}</td><td>{a.stockMinimo ?? 0}</td><td>Q {Number(a.costoPromedio ?? 0).toFixed(2)}</td>
          <td><button type="button" class="link" onclick={() => trasladarAFarmacia(a)}>→ Farmacia POS</button></td>
        </tr>
      {:else}<tr><td colspan="6" class="empty">Sin artículos.</td></tr>{/each}
    </tbody>
  </table>

  <h3>Movimientos rápidos</h3>
  <form onsubmit={registrarMov} class="row">
    <select bind:value={mov.articuloId} required>
      <option value="">— Artículo —</option>
      {#each articulos as a (a.id)}<option value={a.id}>{a.codigo} {a.nombre}</option>{/each}
    </select>
    <select bind:value={mov.tipo}><option>ENTRADA</option><option>SALIDA</option><option>AJUSTE</option><option>MERMA</option><option>TRASLADO</option></select>
    <input type="number" placeholder="Cantidad" bind:value={mov.cantidad} style="width:100px" required/>
    <input type="number" step="0.0001" placeholder="Costo unit." bind:value={mov.costoUnitario} style="width:120px"/>
    <input placeholder="Referencia" bind:value={mov.referencia}/>
    <input placeholder="Motivo" bind:value={mov.motivo}/>
    <button type="submit">Registrar</button>
  </form>

  <h3>Proveedores</h3>
  <form onsubmit={crearProveedor} class="row">
    <input placeholder="NIT" bind:value={nuevoProv.nit} style="width:120px"/>
    <input placeholder="Nombre" bind:value={nuevoProv.nombre} required style="flex:1;min-width:180px"/>
    <input placeholder="Teléfono" bind:value={nuevoProv.telefono} style="width:140px"/>
    <input placeholder="Email" bind:value={nuevoProv.email}/>
    <button type="submit">Agregar</button>
  </form>
  <table>
    <thead><tr><th>NIT</th><th>Nombre</th><th>Teléfono</th><th>Email</th></tr></thead>
    <tbody>
      {#each proveedores as p (p.id)}<tr><td>{p.nit ?? '—'}</td><td>{p.nombre}</td><td>{p.telefono ?? ''}</td><td>{p.email ?? ''}</td></tr>{:else}<tr><td colspan="4" class="empty">No hay proveedores registrados.</td></tr>{/each}
    </tbody>
  </table>
</section>

<style>
  header { display:flex; justify-content:space-between; align-items:center; gap:1rem; flex-wrap:wrap }
  .row { display:flex; gap:.5rem; flex-wrap:wrap; margin:1rem 0; align-items:center }
  .row input, .row select, .row button { padding:.45rem .6rem; border-radius:8px; border:1px solid #ccc; font:inherit }
  .row button { background:#0d6efd; color:#fff; border:0; cursor:pointer }
  .chk { display:flex; align-items:center; gap:.25rem; font-size:.9rem }
  table { width:100%; border-collapse:collapse; margin-bottom:1rem }
  th, td { padding:.5rem .6rem; border-bottom:1px solid #eee; text-align:left; font-size:.9rem }
  tr.bajo { background:#fff4cc }
  .err { color:#b00020 } .empty { color:#888; text-align:center; font-style:italic }
  .ok { color:#1a6b1a; background:#d6f5d6; padding:.35rem .55rem; border-radius:8px; }
  .hint { color:#888; font-size:.82rem; margin:-.4rem 0 .6rem; }
  .csv-btn { display:inline-flex; align-items:center; padding:.45rem .7rem; border:1px dashed #ccc;
    border-radius:8px; cursor:pointer; font-size:.85rem; font-weight:600; color:#0d6efd; background:#fff; }
  .csv-btn:hover { border-color:#0d6efd; background:#eef6ff; }
  .link { background:none; border:0; color:#0d6efd; font-weight:600; cursor:pointer; padding:.2rem .4rem; font-size:.8rem; }
  .link:hover { text-decoration:underline; }
  h3 { margin-top:1.5rem }
</style>
